import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, UserPlus, ArrowRight, Package } from 'lucide-react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import Table from '../components/Table';
import Badge from '../components/Badge';
import Modal from '../components/Modal';
import { ErrMsg } from '../components/FormField';
import { FilterBar, FilterSelect, PageHeader } from '../components/FilterBar';
import SearchableSelect from '../components/SearchableSelect';

const ASSET_CATEGORIES = ['mobile_phone','laptop','desktop','printer','scanner','monitor','tv','accessory','networking','server','tablet','smartwatch','other'];
const today = () => new Date().toISOString().slice(0, 10);

export default function InHandAssets() {
  const { isIT } = useAuth();

  // Assets state
  const [rows, setRows] = useState([]); const [total, setTotal] = useState(0); const [pages, setPages] = useState(1); const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false); const [category, setCategory] = useState('');

  // Accessories state
  const [accRows, setAccRows] = useState([]); const [accTotal, setAccTotal] = useState(0); const [accPages, setAccPages] = useState(1); const [accPage, setAccPage] = useState(1);
  const [accLoading, setAccLoading] = useState(false); const [accCategory, setAccCategory] = useState('');

  const [tab, setTab] = useState('assets');
  const [modal, setModal] = useState(null); const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({}); const [saving, setSaving] = useState(false); const [error, setError] = useState('');
  const [employees, setEmployees] = useState([]);
  const inHandRemarksRef = useRef(null);

  const loadAssets = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (category) params.category = category;
      const r = await api.get('/assets/in-hand', { params });
      setRows(r.data.data || []); setTotal(r.data.total || 0); setPages(r.data.pages || 1);
    } catch {} finally { setLoading(false); }
  }, [page, category]);

  const loadAccessories = useCallback(async () => {
    setAccLoading(true);
    try {
      const params = { page: accPage, limit: 20 };
      if (accCategory) params.category = accCategory;
      const r = await api.get('/accessories/available', { params });
      setAccRows(r.data.data || []); setAccTotal(r.data.total || 0); setAccPages(r.data.pages || 1);
    } catch {} finally { setAccLoading(false); }
  }, [accPage, accCategory]);

  useEffect(() => { loadAssets(); }, [loadAssets]);
  useEffect(() => { loadAccessories(); }, [loadAccessories]);
  useEffect(() => {
    api.get('/employees', { params: { limit: 1000, status: 'active' } }).then(r => setEmployees(r.data.data || [])).catch(() => {});
  }, []);

  const handleAllocateAsset = async e => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      await api.post(`/assets/${selected.id}/assign`, {
        employee_id: form.employee_id,
        allocation_date: form.allocation_date || today(),
        remarks: inHandRemarksRef.current?.value || 'Allocated from in-hand assets'
      });
      setModal(null); setSelected(null); loadAssets();
    } catch (err) { setError(err.response?.data?.error || 'Failed'); } finally { setSaving(false); }
  };

  const handleAllocateAccessory = async e => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      await api.put(`/accessories/${selected.id}`, {
        assigned_to: form.employee_id,
        allocated_date: form.allocation_date || today(),
        status: 'in_use',
        remarks: inHandRemarksRef.current?.value || ''
      });
      setModal(null); setSelected(null); loadAccessories();
    } catch (err) { setError(err.response?.data?.error || 'Failed'); } finally { setSaving(false); }
  };

  const lastHolder = (r) => {
    const assignments = r.assignments || [];
    const last = assignments.find(a => a.returned_date) || assignments[0];
    return last?.Employee?.full_name || '—';
  };

  const assetColumns = [
    { header: 'Asset #', render: r => <span className="font-mono text-xs font-semibold" style={{color:'#60a5fa'}}>{r.asset_number}</span> },
    { header: 'Category', render: r => <Badge value={r.category}/> },
    { header: 'Brand / Model', render: r => <div><div className="font-medium text-sm" style={{color:'#e2e8f0'}}>{r.brand||'—'}</div><div className="text-xs" style={{color:'#334155'}}>{r.model||''}</div></div> },
    { header: 'Serial #', render: r => <span className="font-mono text-xs" style={{color:'#475569'}}>{r.serial_number||'—'}</span> },
    { header: 'Previously With', render: r => <span style={{color:'#94a3b8'}}>{lastHolder(r)}</span> },
    { header: 'Company', render: r => <span className="text-xs font-mono" style={{color:'#334155'}}>{r.Company?.code||'—'}</span> },
    { header: '', render: r => (
      <div className="flex gap-1">
        {isIT && (
          <button onClick={() => { setSelected(r); setForm({ employee_id: '', allocation_date: today(), remarks: '' }); setError(''); setModal('asset'); }}
            className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg font-medium"
            style={{background:'rgba(16,185,129,0.15)',color:'#34d399',border:'1px solid rgba(16,185,129,0.3)'}}>
            <UserPlus size={11}/> Allocate
          </button>
        )}
      </div>
    )}
  ];

  const accColumns = [
    { header: 'Acc #', render: r => <span className="font-mono text-xs font-semibold" style={{color:'#a78bfa'}}>{r.accessory_number||'—'}</span> },
    { header: 'Category', render: r => <span className="text-xs" style={{color:'#94a3b8'}}>{r.category?.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase())||'—'}</span> },
    { header: 'Item', render: r => <div><div className="font-medium text-sm" style={{color:'#e2e8f0'}}>{r.name||`${r.brand||''} ${r.model||''}`}</div><div className="text-xs" style={{color:'#334155'}}>{r.brand||''} {r.model||''}</div></div> },
    { header: 'Serial #', render: r => <span className="font-mono text-xs" style={{color:'#475569'}}>{r.serial_number||'—'}</span> },
    { header: '', render: r => (
      <div className="flex gap-1">
        {isIT && (
          <button onClick={() => { setSelected(r); setForm({ employee_id: '', allocation_date: today(), remarks: '' }); setError(''); setModal('accessory'); }}
            className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg font-medium"
            style={{background:'rgba(139,92,246,0.15)',color:'#a78bfa',border:'1px solid rgba(139,92,246,0.3)'}}>
            <UserPlus size={11}/> Allocate
          </button>
        )}
      </div>
    )}
  ];

  const AllocateForm = ({ onSubmit, type, remarksRef }) => (
    <form onSubmit={onSubmit} className="space-y-4">
      <ErrMsg msg={error}/>
      <div className="px-4 py-3 rounded-xl text-xs" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)'}}>
        {type === 'asset' ? (
          <>
            <div className="font-medium" style={{color:'#e2e8f0'}}>{selected?.asset_number} — {selected?.brand} {selected?.model}</div>
            <div style={{color:'#475569'}}>Serial: {selected?.serial_number||'—'} · {selected?.category?.replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase())}</div>
          </>
        ) : (
          <>
            <div className="font-medium" style={{color:'#e2e8f0'}}>{selected?.accessory_number} — {selected?.name||`${selected?.brand||''} ${selected?.model||''}`}</div>
            <div style={{color:'#475569'}}>Serial: {selected?.serial_number||'—'} · {selected?.category?.replace(/_/g,' ')||'—'}</div>
          </>
        )}
      </div>
      <div>
        <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Allocate To (Employee) *</label>
        <SearchableSelect required value={form.employee_id||''}
          onChange={v => setForm({...form, employee_id: v})}
          options={employees.map(e => ({ value: e.id, label: e.full_name, hint: e.employee_code || undefined }))}
          placeholder="Select employee..."/>
      </div>
      <div>
        <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Allocation Date *</label>
        <input type="date" required value={form.allocation_date||today()} onChange={e => setForm({...form, allocation_date: e.target.value})} className="input-dark"/>
        <div className="text-xs mt-1" style={{color:'#334155'}}>This date will be recorded and shown in the employee profile.</div>
      </div>
      <div>
        <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Remarks</label>
        <textarea ref={remarksRef} defaultValue="" rows={2} className="input-dark" placeholder="e.g. Allocated to new joiner"/>
      </div>
      <div className="flex gap-3">
        <button type="button" onClick={() => { setModal(null); setSelected(null); }} className="btn-ghost flex-1 justify-center">Cancel</button>
        <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center"
          style={{background: type === 'asset' ? 'linear-gradient(135deg,#10b981,#059669)' : 'linear-gradient(135deg,#8b5cf6,#7c3aed)'}}>
          {saving ? 'Allocating...' : `Allocate ${type === 'asset' ? 'Asset' : 'Accessory'}`}
        </button>
      </div>
    </form>
  );

  return (
    <div className="space-y-4">
      <PageHeader title="In-Hand Assets" count={tab === 'assets' ? total : accTotal}>
        <button onClick={() => { loadAssets(); loadAccessories(); }} className="btn-ghost p-2"><RefreshCw size={13}/></button>
      </PageHeader>

      {/* Info banner */}
      <div className="glass rounded-2xl p-4 flex items-start gap-3" style={{border:'1px solid rgba(59,130,246,0.2)',background:'rgba(59,130,246,0.04)'}}>
        <ArrowRight size={16} style={{color:'#60a5fa',flexShrink:0,marginTop:1}}/>
        <div>
          <div className="text-sm font-semibold" style={{color:'#60a5fa'}}>Available for Allocation</div>
          <div className="text-xs mt-0.5" style={{color:'#334155'}}>
            Assets returned from employees and accessories in available status — ready to allocate to new staff. Select an allocation date when allocating.
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-xl w-fit" style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.06)'}}>
        {[['assets','Assets',total],['accessories','Accessories',accTotal]].map(([key,label,count]) => (
          <button key={key} onClick={() => setTab(key)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all"
            style={{background:tab===key?'rgba(59,130,246,0.2)':'transparent',color:tab===key?'#60a5fa':'#475569',border:tab===key?'1px solid rgba(59,130,246,0.3)':'1px solid transparent'}}>
            {key === 'accessories' ? <Package size={13}/> : null}
            {label} <span className="text-xs opacity-70">({count})</span>
          </button>
        ))}
      </div>

      {tab === 'assets' && (
        <>
          <FilterBar hasFilters={!!category} onClear={() => { setCategory(''); setPage(1); }}>
            <FilterSelect value={category} onChange={v => { setCategory(v); setPage(1); }} placeholder="All Categories" options={ASSET_CATEGORIES.map(c => ({value:c,label:c.replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase())}))} />
          </FilterBar>
          <Table columns={assetColumns} data={rows} loading={loading} page={page} pages={pages} total={total} onPage={setPage}/>
        </>
      )}

      {tab === 'accessories' && (
        <>
          <FilterBar hasFilters={!!accCategory} onClear={() => { setAccCategory(''); setAccPage(1); }}>
            <FilterSelect value={accCategory} onChange={v => { setAccCategory(v); setAccPage(1); }} placeholder="All Categories" options={[...new Set(accRows.map(r=>r.category).filter(Boolean))].map(c=>({value:c,label:c.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase())}))} />
          </FilterBar>
          <Table columns={accColumns} data={accRows} loading={accLoading} page={accPage} pages={accPages} total={accTotal} onPage={setAccPage}/>
        </>
      )}

      {modal === 'asset' && (
        <Modal title={`Allocate Asset: ${selected?.asset_number} — ${selected?.brand} ${selected?.model}`} onClose={() => { setModal(null); setSelected(null); }}>
          <AllocateForm onSubmit={handleAllocateAsset} type="asset" remarksRef={inHandRemarksRef}/>
        </Modal>
      )}

      {modal === 'accessory' && (
        <Modal title={`Allocate Accessory: ${selected?.accessory_number} — ${selected?.name||`${selected?.brand||''} ${selected?.model||''}`}`} onClose={() => { setModal(null); setSelected(null); }}>
          <AllocateForm onSubmit={handleAllocateAccessory} type="accessory" remarksRef={inHandRemarksRef}/>
        </Modal>
      )}
    </div>
  );
}
